let requestCounter = 0;

export const createRequestId = (prefix) => {
  requestCounter += 1;
  return `${prefix}-${Date.now()}-${requestCounter}`;
};

export const matchesRequest = (activeRequestId, requestId) =>
  !requestId || activeRequestId === requestId;

const SKIP_DEPS = new Set([
  '@types/react',
  '@types/react-dom',
  '@vitejs/plugin-react',
  'vite',
  'typescript',
]);

const VIRTUAL_ORIGIN = 'https://preview.local/';

let sucrasePromise = null;

async function loadSucrase() {
  if (!sucrasePromise) {
    sucrasePromise = import(
      /* webpackIgnore: true */
      'https://esm.sh/sucrase@3.35.0'
    ).catch((err) => {
      sucrasePromise = null;
      throw new Error('Failed to load JSX transformer: ' + err.message);
    });
  }
  return sucrasePromise;
}

function normalizePath(p) {
  return String(p).replace(/\\/g, '/').replace(/^\.\//, '');
}

function extOf(p) {
  const i = p.lastIndexOf('.');
  return i >= 0 ? p.slice(i + 1).toLowerCase() : '';
}

function buildImportMap(dependencies = {}, devDependencies = {}) {
  const imports = {
    react: 'https://esm.sh/react@19.0.0',
    'react-dom': 'https://esm.sh/react-dom@19.0.0',
    'react-dom/client': 'https://esm.sh/react-dom@19.0.0/client',
    'react/jsx-runtime': 'https://esm.sh/react@19.0.0/jsx-runtime',
    'react/jsx-dev-runtime': 'https://esm.sh/react@19.0.0/jsx-dev-runtime',
  };

  const all = { ...dependencies, ...devDependencies };
  for (const [name, version] of Object.entries(all)) {
    if (SKIP_DEPS.has(name)) continue;
    if (name === 'react' || name === 'react-dom') continue;
    const ver = String(version).replace(/^[\^~>=<\s]+/, '') || 'latest';
    imports[name] = `https://esm.sh/${name}@${ver}`;
    if (name === 'bootstrap') {
      imports[
        'bootstrap/dist/css/bootstrap.min.css'
      ] = `https://esm.sh/bootstrap@${ver}/dist/css/bootstrap.min.css`;
    }
  }
  return imports;
}

function resolveLocal(fromPath, spec, fileIndex) {
  if (!spec.startsWith('.') && !spec.startsWith('/')) return null;
  const fromDir = fromPath.includes('/') ? fromPath.slice(0, fromPath.lastIndexOf('/')) : '';
  let resolved = normalizePath([fromDir, spec].filter(Boolean).join('/'));
  const parts = [];
  for (const part of resolved.split('/')) {
    if (part === '..') parts.pop();
    else if (part && part !== '.') parts.push(part);
  }
  resolved = parts.join('/');

  const candidates = [
    resolved,
    `${resolved}.js`,
    `${resolved}.jsx`,
    `${resolved}.ts`,
    `${resolved}.tsx`,
    `${resolved}/index.js`,
    `${resolved}/index.jsx`,
    `${resolved}/index.ts`,
    `${resolved}/index.tsx`,
  ];
  for (const c of candidates) {
    if (fileIndex.has(c)) return c;
  }
  return null;
}

function virtualUrl(path) {
  return VIRTUAL_ORIGIN + normalizePath(path);
}

function rewriteLocalSpecifiers(code, filePath, fileIndex) {
  const replaceSpec = (spec) => {
    if (!spec.startsWith('.') && !spec.startsWith('/')) return null;
    const local = resolveLocal(filePath, spec, fileIndex);
    if (!local) return null;
    return virtualUrl(local);
  };

  let out = code;

  out = out.replace(/(\bfrom\s+)(['"])(\.[^'"]+|\/[^'"]+)\2/g, (full, fromKw, quote, spec) => {
    const next = replaceSpec(spec);
    return next ? `${fromKw}${quote}${next}${quote}` : full;
  });

  out = out.replace(
    /(\bimport\s*\(\s*)(['"])(\.[^'"]+|\/[^'"]+)\2(\s*\))/g,
    (full, imp, quote, spec, close) => {
      const next = replaceSpec(spec);
      return next ? `${imp}${quote}${next}${quote}${close}` : full;
    },
  );

  out = out.replace(
    /(import\s+)(['"])(\.[^'"]+\.(?:css|scss|sass))\2/g,
    (full, imp, quote, spec) => {
      const next = replaceSpec(spec);
      return next ? `${imp}${quote}${next}${quote}` : full;
    },
  );

  return out;
}

async function transformJs(code, filePath) {
  const sucrase = await loadSucrase();
  const result = sucrase.transform(code, {
    transforms: ['jsx', 'typescript'],
    production: true,
    jsxRuntime: 'automatic',
    filePath,
  });
  return result.code;
}

/**
 * @param {{ files: { path: string, content: string }[] }} sources
 */
export async function buildPreviewDocument(sources) {
  const files = sources.files || [];
  if (!files.length) {
    throw new Error('No source files found in project');
  }

  const fileIndex = new Map();
  for (const f of files) {
    fileIndex.set(normalizePath(f.path), f.content);
  }

  let pkg = { dependencies: {}, devDependencies: {} };
  if (fileIndex.has('package.json')) {
    try {
      pkg = JSON.parse(fileIndex.get('package.json'));
    } catch {
      throw new Error('Invalid package.json');
    }
  }

  const importMap = buildImportMap(pkg.dependencies, pkg.devDependencies);

  const entryCandidates = [
    'src/main.jsx',
    'src/main.js',
    'src/index.jsx',
    'src/index.js',
    'main.jsx',
    'main.js',
    'index.jsx',
    'index.js',
  ];
  const entry = entryCandidates.find((c) => fileIndex.has(c));
  if (!entry) {
    throw new Error('No entry file found (expected src/main.jsx or src/main.js)');
  }

  const cssPaths = [...fileIndex.keys()].filter((p) => ['css', 'scss', 'sass'].includes(extOf(p)));
  let cssCombined = '';
  for (const p of cssPaths) {
    cssCombined += `\n/* ${p} */\n${fileIndex.get(p)}`;
  }

  const jsPaths = [...fileIndex.keys()].filter((p) =>
    ['js', 'jsx', 'mjs', 'cjs', 'ts', 'tsx'].includes(extOf(p)),
  );

  const transformed = {};
  for (const p of jsPaths) {
    try {
      transformed[p] = await transformJs(fileIndex.get(p), p);
    } catch (err) {
      throw new Error(`Transform failed for ${p}: ${err.message}`);
    }
  }

  // Build final module code with virtual absolute imports, then blob URLs
  const blobUrls = {}; // path -> blob:
  const createdUrls = [];

  for (const p of jsPaths) {
    const code = rewriteLocalSpecifiers(transformed[p], p, fileIndex);
    const blob = new Blob([code], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    blobUrls[p] = url;
    createdUrls.push(url);
  }

  for (const p of cssPaths) {
    // CSS side-effect import → empty module (styles already inlined)
    const blob = new Blob([`export default {};\n`], {
      type: 'text/javascript',
    });
    const url = URL.createObjectURL(blob);
    blobUrls[p] = url;
    createdUrls.push(url);
  }

  // Import map: virtual https://preview.local/... → blob:
  const imports = { ...importMap };
  for (const [path, url] of Object.entries(blobUrls)) {
    imports[virtualUrl(path)] = url;
  }

  const entrySpecifier = virtualUrl(entry);

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Preview</title>
  <base href="${VIRTUAL_ORIGIN}" />
  <style>
    html, body, #root { margin: 0; min-height: 100%; }
    body { font-family: system-ui, sans-serif; }
    #preview-error {
      display: none;
      position: fixed; inset: 0;
      background: #1e1e1e; color: #f48771;
      font-family: ui-monospace, monospace;
      font-size: 13px; padding: 16px; overflow: auto; z-index: 99999;
      white-space: pre-wrap;
    }
    #preview-error.show { display: block; }
    #preview-error h1 { color: #f14c4c; font-size: 14px; margin: 0 0 8px; }
  </style>
  <style id="user-css">${cssCombined.replace(/<\/style>/gi, '<\\/style>')}</style>
  <script type="importmap">
${JSON.stringify({ imports }, null, 2)}
  </script>
</head>
<body>
  <div id="root"></div>
  <div id="preview-error"><h1>Preview error</h1><pre id="preview-error-body"></pre></div>
  <script type="module">
    const showError = (err) => {
      const el = document.getElementById('preview-error');
      const body = document.getElementById('preview-error-body');
      el.classList.add('show');
      const msg = (err && (err.stack || err.message || String(err))) || 'Unknown error';
      body.textContent = msg;
      parent.postMessage({
        type: 'preview-error',
        message: err?.message || String(err),
        stack: err?.stack || ''
      }, '*');
    };
    window.addEventListener('error', (e) => showError(e.error || e.message));
    window.addEventListener('unhandledrejection', (e) => showError(e.reason));
    try {
      await import(${JSON.stringify(entrySpecifier)});
      parent.postMessage({ type: 'preview-success' }, '*');
    } catch (err) {
      showError(err);
    }
  </script>
</body>
</html>`;

  return {
    html,
    entry,
    importMap: imports,
    createdUrls,
  };
}

export function revokePreviewUrls(importMapOrUrls) {
  if (!importMapOrUrls) return;
  const list = Array.isArray(importMapOrUrls) ? importMapOrUrls : Object.values(importMapOrUrls);
  for (const url of list) {
    if (typeof url === 'string' && url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    }
  }
}
